import { useAdminAuthStore } from '@/store/admin-auth.store';
import { useTheme } from '@/hooks/useTheme';
import { Badge } from '@/components/ui/Badge';
import { Bell, LogOut, Menu, Moon, Search, Sun } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAdminAuthStore();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b border-border-default bg-surface-raised px-4 backdrop-blur-sm lg:px-6 transition-colors duration-200">
      <button
        onClick={onMenuClick}
        className="rounded-xl p-2 text-text-muted transition-all duration-200 hover:bg-neutral-100 hover:text-text-secondary dark:hover:bg-neutral-800 lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Search */}
      <div className="flex-1">
        {searchOpen ? (
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input
              autoFocus
              type="text"
              placeholder="Rechercher utilisateurs, organisations..."
              className="h-9 w-full rounded-lg border border-border-default bg-surface-inset pl-10 pr-3 text-sm text-text-primary placeholder:text-text-muted transition-all duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              onBlur={() => setSearchOpen(false)}
            />
          </div>
        ) : (
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-text-muted transition-all duration-200 hover:bg-surface-inset hover:text-text-secondary"
          >
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">Rechercher...</span>
            <kbd className="hidden rounded-md border border-border-default bg-surface-inset px-1.5 py-0.5 text-[10px] text-text-muted sm:inline">/</kbd>
          </button>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-1.5">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="rounded-xl p-2 text-text-muted transition-all duration-200 hover:bg-surface-inset hover:text-text-secondary"
          title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
        >
          {theme === 'dark' ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </button>

        <button className="relative rounded-xl p-2 text-text-muted transition-all duration-200 hover:bg-surface-inset hover:text-text-secondary">
          <Bell className="h-4 w-4" />
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-danger-500 ring-2 ring-surface-raised" />
        </button>

        <div className="mx-1 h-6 w-px bg-border-default" />

        <div className="flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <p className="text-xs font-medium text-text-primary">
              {user?.firstName} {user?.lastName}
            </p>
            <Badge variant="brand" className="mt-0.5 text-[10px] px-1.5 py-0">{user?.role}</Badge>
          </div>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-600 to-brand-500 text-xs font-bold text-white shadow-lg shadow-brand-500/20">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="rounded-xl p-2 text-text-muted transition-all duration-200 hover:bg-surface-inset hover:text-danger-400"
          title="Déconnexion"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}
